import { useEffect, useMemo, useState } from 'react'
import { chargerActiveChargeSettingsCollection } from '@/reducers/chargerActiveChargeSettings'
import { useAppDispatch } from '@/hooks'
import { ChargerActiveChargeSettings } from '@/types/activeChargeSettings'
import { VehicleCharger } from '@/types'

type SettingsByChargerId = Record<
  number,
  ChargerActiveChargeSettings | undefined
>

/**
 * A charger is included in the post-enrollment flow only when its enrolled
 * program prescribes a goal. The "goals" are the three plan toggles that
 * `chargeScheduleUtils.scheduleToGoalsDescriptions` already treats as goals.
 */
function hasPrescribedGoal(
  settings: ChargerActiveChargeSettings | undefined,
): boolean {
  if (!settings) {
    return false
  }
  return (
    settings.money_saving_plan_enabled.is_program_prescribed ||
    settings.save_the_planet_plan_enabled.is_program_prescribed ||
    settings.solar_surplus_plan_enabled.is_program_prescribed
  )
}

/**
 * Fetches active charge settings for each charger and returns the subset
 * whose enrolled program prescribes a goal.
 *
 * Implementation uses `Promise.all` over `chargerActiveChargeSettingsCollection.actions.fetch`
 * — calling `useFetch` inside `chargers.map(...)` would violate the rules of hooks.
 *
 * `isLoading` is `true` only while we have *no* settings yet (initial fetch).
 * Subsequent re-fetches caused by a changing charger set update the map in
 * place without flashing a loading state — the previous data stays visible,
 * the new charger's settings just appear when its request resolves. On
 * unmount or charger-id-set change, the in-flight resolution is cancelled so
 * we never call `setState` against a stale set.
 */
export function usePrescribedChargers(chargers: VehicleCharger[]): {
  prescribedChargers: VehicleCharger[]
  isLoading: boolean
} {
  const dispatch = useAppDispatch()
  const [settingsByChargerId, setSettingsByChargerId] =
    useState<SettingsByChargerId>({})
  const [isLoading, setIsLoading] = useState(true)

  // Stable dependency: only re-run when the *set* of charger ids actually changes.
  const chargerIdsKey = chargers
    .map((c) => c.id)
    .sort((a, b) => a - b)
    .join(',')

  useEffect(() => {
    let cancelled = false

    if (chargers.length === 0) {
      setSettingsByChargerId({})
      setIsLoading(false)
      return
    }

    // Only flip into a loading state on the *initial* fetch. Once we have any
    // entries the consumer keeps showing the prior result while we refresh.
    setIsLoading((previous) =>
      Object.keys(settingsByChargerId).length === 0 ? true : previous,
    )

    Promise.all(
      chargers.map(async (charger) => {
        const action = (await dispatch(
          // The fetch action creator accepts `{ id }` and appends `/<id>` to
          // the configured `apiConfig.path`. The dispatched action is async
          // (resolves with the success/failure FSA), so we cast through `any`
          // to satisfy redux's narrower thunk signature.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          chargerActiveChargeSettingsCollection.actions.fetch({
            id: charger.id,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          }) as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        )) as { type?: string; payload?: any } | undefined
        // On failure the action type contains "FAILURE". We treat failure
        // as "no prescription" so the user is never blocked from continuing
        // the flow if the network request happens to fail mid-loop.
        const isFailure =
          typeof action?.type === 'string' && action.type.includes('FAILURE')
        const payload = isFailure
          ? undefined
          : (action?.payload as ChargerActiveChargeSettings | undefined)
        return [charger.id, payload] as const
      }),
    ).then((entries) => {
      if (cancelled) return
      setSettingsByChargerId(Object.fromEntries(entries))
      setIsLoading(false)
    })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, chargerIdsKey])

  const prescribedChargers = useMemo(
    () => chargers.filter((c) => hasPrescribedGoal(settingsByChargerId[c.id])),
    [chargers, settingsByChargerId],
  )

  return { prescribedChargers, isLoading }
}
